import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    fetchCreateInvite,
    fetchInviteInfo,
    fetchListInvites,
    fetchResendInvite,
    fetchRevokeInvite,
} from "./actions";
import {IInvite, IInviteInfo} from "../../../models/IInvites";

interface InvitesState {
    /** Список активных инвайтов компании (для ЛК руководителя). */
    items: IInvite[];
    /** Информация об инвайте, открытом в форме регистрации (публично). */
    currentInfo: IInviteInfo | null;
    /** raw_token + URL, только что созданный/перевыпущенный — показываем модалку "копировать ссылку". */
    lastCreated: { id: string; raw_token: string; invite_url: string } | null;

    loading: boolean;
    creating: boolean;
    error: string | null;
}

const initialState: InvitesState = {
    items: [],
    currentInfo: null,
    lastCreated: null,
    loading: false,
    creating: false,
    error: null,
};

const invitesSlice = createSlice({
    name: "invites",
    initialState,
    reducers: {
        /** Закрыть модалку с raw_token (после копирования или отмены). */
        clearLastCreated(state) {
            state.lastCreated = null;
        },
        /** Сбросить информацию об инвайте (при размонтировании регистр-формы). */
        clearCurrentInfo(state) {
            state.currentInfo = null;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // list
            .addCase(fetchListInvites.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                fetchListInvites.fulfilled,
                (state, action: PayloadAction<IInvite[]>) => {
                    state.loading = false;
                    state.items = action.payload;
                },
            )
            .addCase(fetchListInvites.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // create
            .addCase(fetchCreateInvite.pending, (state) => {
                state.creating = true;
                state.error = null;
            })
            .addCase(fetchCreateInvite.fulfilled, (state, action) => {
                state.creating = false;
                state.lastCreated = action.payload;
                // Список перезагружается цепочкой dispatch'ей в actions.
            })
            .addCase(fetchCreateInvite.rejected, (state, action) => {
                state.creating = false;
                state.error = action.payload as string;
            })
            // revoke
            .addCase(fetchRevokeInvite.fulfilled, (state, action) => {
                state.items = state.items.filter(
                    (i) => i.id !== action.payload.id,
                );
            })
            // resend — НЕ обновляем items вручную (бэк теперь отдаёт
            // ICreateInviteResponse, а не IInvite). Кладём результат в
            // lastCreated → откроется та же модалка "ссылка готова — копировать",
            // что и у create. Актуальный список приедет отдельным
            // fetchListInvites (цепочка в actions).
            .addCase(fetchResendInvite.pending, (state) => {
                state.creating = true;
                state.error = null;
            })
            .addCase(fetchResendInvite.fulfilled, (state, action) => {
                state.creating = false;
                state.lastCreated = action.payload;
            })
            .addCase(fetchResendInvite.rejected, (state, action) => {
                state.creating = false;
                state.error = action.payload as string;
            })
            // info (для публичной формы)
            .addCase(fetchInviteInfo.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.currentInfo = null;
            })
            .addCase(fetchInviteInfo.fulfilled, (state, action) => {
                state.loading = false;
                state.currentInfo = action.payload;
            })
            .addCase(fetchInviteInfo.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearLastCreated, clearCurrentInfo } = invitesSlice.actions;
export default invitesSlice.reducer;